// JS port of functions/core/format_utils.py:update_body_html() and
// get_html_bullet_for_field() — keep in sync with the Python source.
//
// All functions are pure (no React, no Firestore). Safe to use in useMemo.

import { normalizeCategoryKey, conditionDisplayLabel } from '../productEditConstants';
import { DEFAULT_CATEGORY_CONFIGS } from './defaultCategoryConfigs';

const CERT_FULL_NAMES = {
  GIA: 'Gemological Institute of America',
  IGI: 'International Gemological Institute',
  AGS: 'American Gem Society',
  EGL: 'European Gemological Laboratory',
  HRD: 'HRD Antwerp',
};

function extractCertAcronym(certifierStr) {
  if (!certifierStr) return '';
  const upper = certifierStr.toUpperCase();
  for (const acronym of Object.keys(CERT_FULL_NAMES)) {
    const re = new RegExp('\\b' + acronym + '\\b');
    if (re.test(upper)) return acronym;
  }
  const first = certifierStr.trim().split(/\s+/)[0];
  return first ? first.toUpperCase() : '';
}

// Mirrors condition_string() in format_utils.py
function conditionString(condition, category) {
  const cat = category || '';
  if (cat.includes('Watches')) {
    if (condition === 'Excellent') return 'Very minimal wear from careful handling. May have faint hairline scratches on the case or bracelet, with a clean crystal and tight bracelet. Functions perfectly.';
    if (condition === 'Great') return 'Light wear visible from normal use. May show small scratches on the case, bezel, or clasp, and slight strap or bracelet wear. Crystal is clear with no major marks.';
    if (condition === 'Very Good') return 'Noticeable wear from regular use. May have visible scratches, light crystal marks, or stretched bracelets. Leather straps may show aging. Still fully functional.';
    if (condition === 'Good') return 'Moderate to heavy wear. May show deeper scratches, worn bezels, crystal marks, or stretched/worn straps. Still wearable and functional but shows age.';
  } else if (cat.includes('Jewelry')) {
    if (condition === 'Excellent') return 'Shows minimal surface wear, no stone wear, maintaining full shine and integrity.';
    if (condition === 'Great') return 'May show fine scratches, but remains well-kept.';
    if (condition === 'Very Good') return 'Will show scratches on metal, minor stone wear.';
    if (condition === 'Good') return 'Jewelry will have visible wear, stones will show scratches, worn or missing prongs, but remain intact.';
  } else {
    if (condition === 'Excellent') return "Will show very minimal signs of wear, usually due to storage or minimal use. May have small miniscule marks on exterior, interior and hardware, but overall looks like it's new.";
    if (condition === 'Great') return 'Will show some signs of wear such as marks on exterior, interior and hardware, usually small buffs, scratches or stains, but still overall presents itself as a newer item with some use.';
    if (condition === 'Very Good') return 'Will show general signs of wear and small watermarks on materials, light wear on corners, closure or zippers, minimal interior spotting due to use and aging.';
    if (condition === 'Good') return 'Will show moderate signs of wear including: watermarks, scratching, scuffs, indentations, wrinkles, corner wear, hardware scratching and buffs, interior spotting or staining, minimal structure loss due to use and aging or incorrect storage.';
  }
  return "Will show very minimal signs of wear, usually due to storage or minimal use. May have small miniscule marks on exterior, interior and hardware, but overall looks like it's new.";
}

// Mirrors convert_condition() in format_utils.py
function convertCondition(conditionKey) {
  if (['NEW', 'NEW_OTHER', 'NEW_WITH_DEFECTS', 'USED_EXCELLENT'].includes(conditionKey)) return 'Excellent';
  if (conditionKey === 'USED_GOOD') return 'Great';
  if (conditionKey === 'USED_ACCEPTABLE') return 'Very Good';
  if (conditionKey === 'FOR_PARTS_OR_NOT_WORKING') return 'Good';
  return conditionKey || '';
}

// Parse a dimensions data object into a human-readable string.
// dimensionsData = { length, width, height, weight, lengthUnit, weightUnit, ... }
export function dimensionsString(dimensionsData) {
  if (!dimensionsData) return '';
  const { length, width, height, dimensionsUnit = 'inches', weight, weightUnit = 'pounds' } = dimensionsData;
  let parts = [];
  if (length && width && height) {
    parts.push(`${length}" x ${width}" x ${height}" ${dimensionsUnit}`);
  }
  if (weight) {
    parts.push(`${weight} ${weightUnit}`);
  }
  return parts.join(', ');
}

// Parse item weight string to grams (mirrors WeightConverter.to_canonical().grams)
function itemWeightToGrams(weightStr) {
  if (!weightStr) return null;
  const s = weightStr.toString().trim().toLowerCase();
  const num = parseFloat(s);
  if (isNaN(num)) return null;
  if (s.includes('kg')) return Math.round(num * 1000);
  if (s.includes('oz') || s.includes('ounce')) return Math.round(num * 28.3495);
  if (s.includes('lb') || s.includes('pound')) return Math.round(num * 453.592);
  if (s.includes('g') || s.includes('gram')) return Math.round(num);
  // default assume grams
  return Math.round(num);
}

// Mirrors get_html_bullet_for_field() in format_utils.py
function getBulletForField(bulletFieldDef, cd, dimensionsData) {
  const { fieldPath, format, specialType, candidates, enabled } = bulletFieldDef;
  if (!enabled) return null;

  if (specialType === 'entropy') {
    return '<li>Comes with <a href="https://www.theluxuryexchangepdx.com/pages/entrupy-authentication" title="Entrupy Authentication" rel="noopener" target="_blank">Entrupy Certificate of Authenticity</a></li>\n';
  }

  if (specialType === 'dimensions') {
    const dimStr = dimensionsString(dimensionsData);
    if (!dimStr) return null;
    return `<li>${dimStr}</li>\n`;
  }

  if (specialType === 'item_weight') {
    const raw = cd[fieldPath] || cd['Item Weight'] || '';
    const grams = itemWeightToGrams(raw);
    if (grams == null) return null;
    return `<li>${grams} grams</li>\n`;
  }

  if (specialType === 'country') {
    const val = cd[fieldPath] || cd['Country/Region of Manufacture'] || '';
    if (!val) return null;
    return `<li>Made In ${val}</li>\n`;
  }

  if (specialType === 'diamond_carat') {
    const carat = cd['Total Carat Weight'] || '';
    if (!carat) return null;
    const labGrown = (cd['Main Stone Creation'] || '').trim().toLowerCase();
    const isLab = ['yes', 'true', 'lab', 'lab-grown', 'lab grown', 'laboratory grown', 'laboratory-grown'].includes(labGrown);
    const diamondType = isLab ? 'Lab-Grown' : 'Natural';
    return `<li>${carat}ct ${diamondType} Diamond</li>\n`;
  }

  if (specialType === 'certification_status') {
    const status = (cd['Certification Status'] || '').toUpperCase().trim();
    const certifier = extractCertAcronym(cd['Certification'] || '');
    if (status === 'DIA CERT') {
      const label = certifier ? `${certifier} Certified` : 'Diamond Certified';
      return `<li>${label}</li>\n`;
    }
    if (status === 'MULTI CERT') {
      const label = certifier ? `${certifier} Certified Diamond` : 'Diamond Certified';
      return `<li>${label}</li>\n`;
    }
    return null;
  }

  if (specialType === 'first_of') {
    for (const candidate of (candidates || [])) {
      const val = cd[candidate];
      if (val && val.length > 0) {
        if (format) return `<li>${format.replace('<value>', val)}</li>\n`;
        return `<li>${val}</li>\n`;
      }
    }
    return null;
  }

  if (specialType === 'combo') {
    let parts = [];
    for (const candidate of (candidates || [])) {
      const val = cd[candidate] || '';
      if (val) parts.push(val);
    }
    if (parts.length === 0) return null;
    return `<li>${parts.join(' with ')}</li>\n`;
  }

  // Standard field lookup
  const key = fieldPath;
  const val = cd[key];
  if (!val || val.length === 0) return null;
  if (format) return `<li>${format.replace('<value>', val)}</li>\n`;
  return `<li>${val}</li>\n`;
}

/**
 * Generate Shopify Body HTML from product data.
 *
 * @param {Object} consolidatedData        — product.consolidatedData (key-value dict of specs)
 * @param {Object} dimensionsData          — product.gptDimensionsData (length/width/height/weight)
 * @param {Object} priceOptions            — product.priceOptions (e.g. { MSRP: '500' })
 * @param {Object|null} template           — CategoryConfig.bodyHtmlTemplate, or null to use default
 * @param {string|null} shopifyCategoryFullName — product.shopifyCategory.fullName (authoritative category)
 * @returns {string} HTML string
 */
export function generateBodyHtml(consolidatedData, dimensionsData, priceOptions, template, shopifyCategoryFullName) {
  const cd = consolidatedData || {};

  // Mirrors format_utils.py: shopifyCategory.fullName is authoritative (customer-confirmed Shopify
  // taxonomy); fall back to cd fields. cd['Category'] is AI-guessed and can be wrong.
  const effectiveCat = shopifyCategoryFullName || cd['Shopify Category'] || cd['Category'] || '';

  // Resolve the template: use provided, or fall back to default for this category
  let tmpl = template;
  if (!tmpl) {
    const key = normalizeCategoryKey(effectiveCat);
    tmpl = DEFAULT_CATEGORY_CONFIGS[key]?.bodyHtmlTemplate
      || DEFAULT_CATEGORY_CONFIGS.default.bodyHtmlTemplate;
  }

  const description = cd[tmpl.descriptionField?.replace('consolidatedData.', '')] || cd['Description'] || '';
  let html = `<p>${description}</p>\n<ul>\n`;

  for (const bulletField of (tmpl.bulletFields || [])) {
    const line = getBulletForField(bulletField, cd, dimensionsData);
    if (line) html += line;
  }

  // MSRP — only shown when a brand is present
  const brand = (cd['Brand'] || '').trim();
  if (priceOptions && brand) {
    const msrp = priceOptions['MSRP'] || priceOptions['msrp'];
    if (msrp) html += `<li>Est. MSRP: ${msrp}</li>\n`;
  }

  html += '</ul>\n';

  // Condition block
  const conditionKey = cd[tmpl.conditionField?.replace('consolidatedData.', '')] || cd['Condition'] || '';
  const conditionBucket = convertCondition(conditionKey);
  const conditionLabel = conditionDisplayLabel(conditionKey);
  html += `<p><strong>Condition</strong>: ${conditionLabel} - ${conditionString(conditionBucket, effectiveCat)}</p>\n`;

  // Jewelry certification blurb
  if (tmpl.appendJewelryCertBlurb) {
    const certStatus = (cd['Certification Status'] || 'NO CERT').toUpperCase().trim();
    const certifierRaw = (cd['Certification'] || '').trim();
    const certifier = extractCertAcronym(certifierRaw);
    const hasColoredStones = !!(cd['Colored Stones'] || '').trim();
    const certFull = CERT_FULL_NAMES[certifier] || certifierRaw;
    const isCertified = ['DIA CERT', 'MULTI CERT'].includes(certStatus);

    if (isCertified && !hasColoredStones) {
      html += `<p>The diamond is ${certifier} certified, confirming its carat weight, color, clarity, and cut as independently verified by the ${certFull}.</p>\n`;
    } else if (isCertified && hasColoredStones) {
      html += `<p>This piece features a ${certifier}-certified diamond, ensuring verified quality and authenticity. The accompanying colored gemstones are described based on visual assessment and have not been certified by a gemologist. Their pricing reflects their beauty and quality as presented, not as certified natural or untreated gemstones.</p>\n`;
    } else {
      html += '<p>Colored gemstones are identified and described to the best of our professional judgment based on visual assessment. Unless otherwise stated, stones have not been certified by a gemologist. Pricing reflects their beauty and quality as presented, not as certified natural or untreated gemstones.</p>\n';
    }
  }

  return html;
}
